/**
 * Mensajes amigables para SSE / eventos WhatsApp (sin datos técnicos).
 */
import { formatDisplayPhone } from "layouts/whatsapp/components/NotificationRecipientsSummary";

const EVENT_META = {
  "session.connected": { icon: "✅", title: "Sesión conectada", color: "success" },
  "session.qr": { icon: "📱", title: "QR actualizado", color: "info" },
  "message.sent": { icon: "✅", title: "Mensaje enviado", color: "success" },
  "message.failed": { icon: "❌", title: "Error al enviar mensaje", color: "error" },
  "session.disconnected": { icon: "⚠️", title: "Sesión desconectada", color: "warning" },
  "session.invalid_number": { icon: "❌", title: "Número inválido", color: "error" },
  "session.reconnecting": { icon: "🔄", title: "Reconectando sesión", color: "info" },
  "session.validating": { icon: "🔎", title: "Validando número", color: "info" },
  "validation.failed": { icon: "❌", title: "Validación fallida", color: "error" },
  "queue.updated": { icon: "📤", title: "Procesando cola", color: "info" },
  "queue.processed": { icon: "📤", title: "Procesando cola", color: "info" },
  "session.recovery": { icon: "🔄", title: "Reconectando sesión", color: "info" },
  "session.auth_failure": { icon: "⚠️", title: "Sesión desconectada", color: "warning" },
  "stream.error": { icon: "❌", title: "Error de conexión", color: "error" },
};

function extractPhone(payload) {
  if (!payload || typeof payload !== "object") return null;
  const raw =
    payload.telefonoDestino ||
    payload.telefono_destino ||
    payload.connectedPhone ||
    payload.phone ||
    null;
  return raw ? formatDisplayPhone(String(raw)) : null;
}

function humanDisconnectReason(reason) {
  const map = {
    paused: "Conexión pausada temporalmente",
    manual_logout: "Sesión cerrada correctamente",
    manual_disconnect: "Sesión cerrada",
    connection_close: "Se perdió la conexión",
    logged_out: "Sesión cerrada en WhatsApp",
  };
  return map[reason] || "Sesión desconectada";
}

export function getEventMeta(type) {
  return (
    EVENT_META[type] || {
      icon: "•",
      title: "Actividad del sistema",
      color: "default",
    }
  );
}

export function formatEventMessage(type, payload) {
  const phone = extractPhone(payload);

  switch (type) {
    case "session.connected":
      return phone ? `WhatsApp vinculado correctamente` : "WhatsApp vinculado correctamente";
    case "session.qr":
      return "Escanea el código QR con tu celular para vincular";
    case "message.sent":
      return phone ? `Mensaje enviado a ${phone}` : "Mensaje enviado correctamente";
    case "message.failed":
      return phone ? `Error enviando a ${phone}` : "No se pudo enviar el mensaje";
    case "session.disconnected":
    case "session.auth_failure":
      return humanDisconnectReason(payload?.reason);
    case "session.invalid_number":
    case "validation.failed":
      return "El número conectado no coincide con tu número principal registrado";
    case "session.reconnecting":
    case "session.recovery":
      return "Restableciendo conexión con WhatsApp…";
    case "session.validating":
      return "Verificando que el número sea el correcto…";
    case "queue.updated":
    case "queue.processed":
      if (payload?.action === "batch_claimed" || payload?.processed) {
        return "Procesando alertas pendientes…";
      }
      return "Cola de mensajes actualizada";
    default:
      return getEventMeta(type).title;
  }
}

export const STATUS_UI = {
  enviado: { label: "Enviado", emoji: "✅", color: "success" },
  error: { label: "Error", emoji: "❌", color: "error" },
  pendiente: { label: "Pendiente", emoji: "⏳", color: "warning" },
  enviando: { label: "Enviando", emoji: "📤", color: "info" },
  retry: { label: "Reintento", emoji: "🔄", color: "warning" },
  cancelado: { label: "Cancelado", emoji: "—", color: "default" },
};

export function getStatusUi(displayStatus) {
  return STATUS_UI[displayStatus] || { label: displayStatus || "—", emoji: "•", color: "default" };
}

export function formatOrigenLabel(origen) {
  if (origen === "numeros_rebote") return "Rebote";
  if (origen === "usuarios_secundarios") return "Secundario";
  return "—";
}

export function formatMonto(monto) {
  const n = Number(monto);
  if (!Number.isFinite(n)) return "—";
  return `S/${n.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatSessionLabel(session, audit = null) {
  if (session?.connected || session?.status === "connected") return "Conectada";
  if (audit?.session_status === "connected") return "Conectada";
  if (!session || Object.keys(session).length === 0) return "Inactiva";
  if (session.status === "paused" || session.paused) return "Pausada";
  if (session.status === "awaiting_qr") return "Esperando QR";
  if (session.status === "disconnected") return "Desconectada";
  if (session.status === "reconnecting" || session.status === "validating") {
    return session.connectedPhone ? "Conectada" : "Reconectando";
  }
  if (session.connectedPhone && session.status !== "disconnected") return "Conectada";
  return "Inactiva";
}

export function formatEngineLabel(engine) {
  if (!engine) return "Baileys";
  const e = String(engine).toLowerCase();
  if (e === "baileys") return "Baileys";
  if (e === "openwa") return "OpenWA";
  return "WhatsApp";
}
